import React, { useState } from "react";
import {
  Grid,
  Box,
  Modal,
  Card,
  Typography,
  Button,
  TextField,
} from "@mui/material";
import { theme } from "../../../theme";
import colors from "../../../colors";
import { useTranslation } from "react-i18next";
import { Dayjs } from "dayjs";

export interface INewUserInfo {
  email: string;
  firstName: string;
  lastName: string;
  // additional fields
  company?: string;
  phoneNumber?: string;
  dateOfBirth?: Date;
  taxNumber?: string;
  tosAccepted?: boolean;
  occupationalSafetyCard?: string;
  hotWorkCard?: string;
  firstAidCardImage?: string;
  occupationalSafetyCardImage?: string;
  hotWorkCardImage?: string;
  shoeSize?: string;
  jacketSize?: string;
  dietaryRestrictions?: string;
  visitationDate?: string;
}

interface IQrUserVerificationModalProps {
  newUserFields: string[];
  checkUser: (name: string) => Promise<{ userId: string } | undefined>;
  isVisible: boolean;
  setVisible: (val: boolean) => void;
  saveUser: (userId: string) => void;
  addUser: (role: INewUserInfo) => Promise<boolean>;
  newUser: boolean;
  sendVerificationEmail: (email: string) => Promise<boolean>;
  checkVerificationString: (
    email: string,
    verificationString: string
  ) => Promise<boolean>;
}

export const QrUserVerificationModal = (
  props: IQrUserVerificationModalProps
) => {
  const { t } = useTranslation();
  // username; email or phone number
  const [username, setUsername] = useState("");
  const [verificationString, setVerificationString] = useState("");
  const [error, setError] = useState<string | undefined>(undefined);

  const [giveEmailView, setGiveEmailView] = useState(true);
  const [addUserView, setAddUserView] = useState(false);
  const [verifyEmailView, setVerifyEmailView] = useState(false);
  const [checkEmailVerificationView, setCheckEmailVerificationView] =
    useState(false);

  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [company, setCompany] = useState<string>("");
  const [email, setEmail] = useState<string>("");

  // other fields, check playCourse.

  const [taxNumber, setTaxNumber] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState<Dayjs | undefined>();
  const [occupationalSafetyCard, setOccupationalSafetyCard] =
    useState<string>("");
  const [hotWorkCard, setHotWorkCard] = useState<string>("");
  const [occupationalSafetyCardImage, setOccupationalSafetyCardImage] =
    useState<string>("");
  const [firstAidCardImage, setFirstAidCardImage] = useState<string>("");
  const [hotWorkCardImage, setHotWorkCardImage] = useState<string>("");
  const [shoeSize, setShoeSize] = useState("");
  const [jacketSize, setJacketSize] = useState("");
  const [dietaryRestrictions, setDietaryRestrictions] = useState("");
  const [visitationDate, setVisitationDate] = useState("");

  const textBoxWidth = 320;

  const saveButtonDisabled = () => {
    if (!(email && firstName && lastName)) {
      return true;
    } else if (
      props.newUserFields.some((item) =>
        item.toLowerCase().includes("phone")
      ) &&
      !phoneNumber
    ) {
      return true;
    } else if (
      props.newUserFields.some((item) =>
        item.toLowerCase().includes("taxnumber")
      ) &&
      !taxNumber
    ) {
      return true;
    } else if (
      props.newUserFields.some((item) =>
        item.toLowerCase().includes("occupationalsafetycardvisible")
      ) &&
      !occupationalSafetyCard
    ) {
      return true;
    } else if (
      props.newUserFields.some((item) =>
        item.toLowerCase().includes("hotworkcardvisible")
      ) &&
      !hotWorkCard
    ) {
      return true;
    } else if (
      props.newUserFields.some((item) =>
        item.toLowerCase().includes("shoesize")
      ) &&
      !shoeSize
    ) {
      return true;
    } else if (
      props.newUserFields.some((item) =>
        item.toLowerCase().includes("jacketsize")
      ) &&
      !jacketSize
    ) {
      return true;
    } else if (
      props.newUserFields.some((item) =>
        item.toLowerCase().includes("visitationdate")
      ) &&
      !visitationDate
    ) {
      return true;
    } else return false;
  };

  // Checks given username against the invitations of the course, and if it exists, returns the user id
  const checkUsername = async () => {
    const user = await props.checkUser(username);
    if (user?.userId) {
      props.saveUser(user?.userId);
      props.setVisible(false);
    } else {
      // If the user does not exist, check if newUser-prop is on, and show add user -view
      if (props.newUser) {
        // save username to email-field
        setEmail(username);
        // show add user - view.
        setVerifyEmailView(true);
        // hide GiveEmailView
        setGiveEmailView(false);
      }
      // If the newUser-prop is off, don't let the user play the course.
      else setError("User does not exist.");
    }
  };

  const verifyEmail = async () => {
    console.log("Send verification email to: ", username);
    const emailSent = await props.sendVerificationEmail(username);
    if (emailSent) {
      setCheckEmailVerificationView(true);
      setVerifyEmailView(false);
    } else {
      setError("email verification failed");
      // Go back to email view?
    }
  };

  const checkVerificationCode = async () => {
    const checked = await props.checkVerificationString(
      username,
      verificationString
    );

    if (checked) {
      setCheckEmailVerificationView(false);
      setAddUserView(true);
      setEmail(username); // set username to email.
    } else {
      setError("email verification failed");
      // Go back to email view?
    }
  };

  const goBack = () => {
    // Go back to the start modal, if user cancels verification.
    setGiveEmailView(true);
    setVerifyEmailView(false);
    setCheckEmailVerificationView(false);
  };

  const validateInput = (input: string) => {
    const regex = /(^\d+$)|(@)/;
    const validated = regex.test(input);
    return validated;
  };

  if (giveEmailView)
    // View for checking the email
    return (
      <Modal
        open={props.isVisible}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Card
          style={{
            padding: theme.spacing(3),
            background: colors.backgroundSecondary,
          }}
        >
          <Typography>{t("askUserName")}</Typography>

          <Box
            sx={{
              flex: 1,
              flexDirection: "row",
              justifyContent: "center",
              justifyItems: "center",
              alignContent: "center",
            }}
          >
            <TextField
              label={t("askEmail")}
              margin="normal"
              style={{ minWidth: textBoxWidth }}
              value={username}
              error={!!error || !validateInput(username)}
              onChange={(e) => {
                setEmail(e.target.value);
                setUsername(e.target.value);
                setError(undefined);
              }}
            />

            <Button
              variant="text"
              style={{
                marginTop: theme.spacing(3),
                alignSelf: "center",
                justifySelf: "center",
              }}
              onClick={() => {
                checkUsername();
              }}
            >
              {t("ok")}
            </Button>
          </Box>
        </Card>
      </Modal>
    );
  else if (verifyEmailView)
    // View for checking the email
    return (
      <Modal
        open={props.isVisible}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Card
          style={{
            maxWidth: "80%",
            padding: theme.spacing(3),
            background: colors.backgroundSecondary,
          }}
        >
          <Typography>{t("willSendVerificationCode")}</Typography>

          <Box
            sx={{
              flex: 1,
              flexDirection: "row",
              justifyContent: "center",
              justifyItems: "center",
              alignContent: "center",
            }}
          >
            <TextField
              label={t("email")}
              margin="normal"
              style={{ minWidth: textBoxWidth }}
              value={username}
              error={!!error || !validateInput(username)}
              onChange={(e) => {
                setUsername(e.target.value);
                setError(undefined);
              }}
            />

            <Button
              variant="text"
              style={{
                marginTop: theme.spacing(3),
                alignSelf: "center",
                justifySelf: "center",
              }}
              onClick={() => {
                verifyEmail();
              }}
            >
              {t("sendVerificationCode")}
            </Button>

            <Button
              variant="text"
              style={{
                marginTop: theme.spacing(3),
                alignSelf: "center",
                justifySelf: "center",
              }}
              onClick={() => {
                goBack();
              }}
            >
              {t("cancel")}
            </Button>
          </Box>
        </Card>
      </Modal>
    );
  else if (checkEmailVerificationView)
    // View for checking the verification code
    return (
      <Modal
        open={props.isVisible}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          
        }}
      >
        <Card
          style={{
            padding: theme.spacing(3),
            background: colors.backgroundSecondary,
            
          }}
        >
          
          <Typography>{t("askToVerify")}</Typography>
          <Typography>{t("askToVerifySpam")}</Typography>
          <Typography>{t("askToVerifySupport")}</Typography>

          <Box
            sx={{
              flex: 1,
              flexDirection: "row",
              justifyContent: "center",
              justifyItems: "center",
              alignContent: "center",
            }}
          >
            <TextField
              label={t("verificationCode")}
              margin="normal"
              style={{ minWidth: textBoxWidth }}
              value={verificationString}
              error={!!error}
              onChange={(e) => {
                setVerificationString(e.target.value);
                setError(undefined);
              }}
            />

            <Button
              variant="text"
              style={{
                marginTop: theme.spacing(3),
                alignSelf: "center",
                justifySelf: "center",
              }}
              onClick={() => {
                checkVerificationCode();
              }}
            >
              {t("ok")}
            </Button>

            <Button
              variant="text"
              style={{
                marginTop: theme.spacing(3),
                alignSelf: "center",
                justifySelf: "center",
              }}
              onClick={() => {
                goBack();
              }}
            >
              {t("cancel")}
            </Button>
          </Box>
        </Card>
      </Modal>
    );
  else if (addUserView)
    // View for adding a new user.
    return (
      <Modal
        open={props.isVisible}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow:"auto",
        }}
      >
        <Card
          style={{
            padding: theme.spacing(3),
            background: colors.backgroundSecondary,
            maxHeight: "80%",
            overflow:"auto"
          }}
        >
          <Typography>{t("addUser")}</Typography>

          <Box
            width={"100%"}
            height={"100%"}
            position={"relative"}
            display={"flex"}
            flexDirection={"column"}
            alignItems={"flex-start"}
          >
            <TextField
              sx={{ flex: 1 }}
              label={t("email")}
              margin="normal"
              style={{ minWidth: textBoxWidth }}
              error={!!email && !validateInput(email)}
              disabled // Email can't be changed after verification.
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError(undefined);
              }}
            />
            <TextField
              sx={{ flex: 1 }}
              label={t("firstName")}
              margin="normal"
              style={{ minWidth: textBoxWidth }}
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
                setError(undefined);
              }}
            />
            <TextField
              label={t("lastName")}
              margin="normal"
              style={{ minWidth: textBoxWidth }}
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
                setError(undefined);
              }}
            />
            {(props.newUserFields.some((item) =>
              item.toLowerCase().includes("companyvisible")
            ) ||
              props.newUserFields.length == 0) && (
              <TextField
                label={t("company")}
                margin="normal"
                style={{ minWidth: textBoxWidth }}
                value={company}
                onChange={(e) => {
                  setCompany(e.target.value);
                  setError(undefined);
                }}
              />
            )}
            {props.newUserFields.some((item) =>
              item.toLowerCase().includes("taxnumbervisible")
            ) && (
              <TextField
                label={t("taxNumber")}
                margin="normal"
                style={{ minWidth: textBoxWidth }}
                value={taxNumber}
                onChange={(e) => {
                  setTaxNumber(e.target.value);
                  setError(undefined);
                }}
              />
            )}
            {props.newUserFields.some((item) =>
              item.toLowerCase().includes("phonevisible")
            ) && (
              <TextField
                label={t("phoneNumber")}
                margin="normal"
                style={{ minWidth: textBoxWidth }}
                value={phoneNumber}
                onChange={(e) => {
                  setPhoneNumber(e.target.value);
                  setError(undefined);
                }}
              />
            )}
            {props.newUserFields.some((item) =>
              item.toLowerCase().includes("occupationalsafetycardvisible")
            ) && (
              <TextField
                label={t("occupationalSafetyCard")}
                margin="normal"
                style={{ minWidth: textBoxWidth }}
                value={occupationalSafetyCard}
                onChange={(e) => {
                  setOccupationalSafetyCard(e.target.value);
                  setError(undefined);
                }}
              />
            )}
            {props.newUserFields.some((item) =>
              item.toLowerCase().includes("hotworkcardvisible")
            ) && (
              <TextField
                label={t("hotWorkCard")}
                margin="normal"
                style={{ minWidth: textBoxWidth }}
                value={hotWorkCard}
                onChange={(e) => {
                  setHotWorkCard(e.target.value);
                  setError(undefined);
                }}
              />
            )}
            {props.newUserFields.some((item) =>
              item.toLowerCase().includes("shoesizevisible")
            ) && (
              <TextField
                label={t("shoeSize")}
                margin="normal"
                style={{ minWidth: textBoxWidth }}
                value={shoeSize}
                onChange={(e) => {
                  setShoeSize(e.target.value);
                  setError(undefined);
                }}
              />
            )}
            {props.newUserFields.some((item) =>
              item.toLowerCase().includes("jacketsizevisible")
            ) && (
              <TextField
                label={t("jacketSize")}
                margin="normal"
                style={{ minWidth: textBoxWidth }}
                value={jacketSize}
                onChange={(e) => {
                  setJacketSize(e.target.value);
                  setError(undefined);
                }}
              />
            )}
            {props.newUserFields.some((item) =>
              item.toLowerCase().includes("dietaryrestrictionsvisible")
            ) && (
              <TextField
                label={t("dietaryRestrictions")}
                margin="normal"
                style={{ minWidth: textBoxWidth }}
                value={dietaryRestrictions}
                onChange={(e) => {
                  setDietaryRestrictions(e.target.value);
                  setError(undefined);
                }}
              />
            )}
            {props.newUserFields.some((item) =>
              item.toLowerCase().includes("visitationdatevisible")
            ) && (
              <TextField
                label={t("visitationDate")}
                margin="normal"
                style={{ minWidth: textBoxWidth }}
                value={visitationDate}
                onChange={(e) => {
                  setVisitationDate(e.target.value);
                  setError(undefined);
                }}
              />
            )}

            <Button
              variant="text"
              style={{
                marginTop: theme.spacing(3),
                alignSelf: "center",
                justifySelf: "center",
              }}
              disabled={saveButtonDisabled()}
              onClick={() => {
                props.addUser({
                  firstName: firstName,
                  lastName: lastName,
                  company: company,
                  email: email,
                  taxNumber: taxNumber,
                  phoneNumber: phoneNumber,
                  //dateOfBirth: dateOfBirth,
                  occupationalSafetyCard: occupationalSafetyCard,
                  hotWorkCard: hotWorkCard,
                  shoeSize: shoeSize,
                  jacketSize: jacketSize,
                  dietaryRestrictions: dietaryRestrictions,
                  visitationDate: visitationDate,
                });
              }}
            >
              {t("save")}
            </Button>
          </Box>
        </Card>
      </Modal>
    );
  else return <></>;
};
